import { DSCardWrapper } from "@components/design-system/molecules";
import {
  faArrowRight,
  faShareNodes,
} from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import mixpanel from "mixpanel-browser";
import { useState } from "react";
import { useWatch } from "react-hook-form";
import { useNavigate } from "react-router-dom";

import NameAndSaveStep from "./NameAndSaveStep";
import PreviewStep from "./PreviewStep";
import StartingPointStep from "./StartingPointStep";
import TemplatePreferencesStep from "./TemplatePreferencesStep";
import TemplateStructureStep from "./TemplateStructureStep";
import WizardStepIndicator from "./WizardStepIndicator";
import { DSModal } from "../../design-system/organisms/DSModal";

const WIZARD_STEPS = {
  STARTING_POINT: 1,
  TEMPLATE_STRUCTURE: 2,
  TEMPLATE_PREFERENCES: 3,
  PREVIEW: 4,
  NAME_AND_SAVE: 5,
};

export default function TemplateWizard({
  control,
  loadingExtraction,
  loadingPreview,
  previewContent,
  handleFileUpload,
  handlePreview,
  handleClearSampleTranscript,
  previewSource,
  setPreviewSource,
  selectedNote,
  onSelectNote,
  onSave,
  saving,
  clearPreview,
  showSuccessModal,
  onCloseSuccessModal,
  onShareTemplate,
}) {
  const [currentStep, setCurrentStep] = useState(
    WIZARD_STEPS.STARTING_POINT,
  );
  const [selectedMethod, setSelectedMethod] = useState(null);
  const templateUuid = useWatch({ control, name: "uuid" });
  const navigate = useNavigate();

  const trackStepTransition = (from, to) => {
    mixpanel.track("Wizard Step Transition", {
      templateUuid,
      fromStep: from,
      toStep: to,
    });
  };

  const goToStep = (step) => {
    trackStepTransition(currentStep, step);
    setCurrentStep(step);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  return (
    <div className="my-lg">
      <DSCardWrapper
        padding="p-lg"
        variant="ghost"
        data-testid="template-wizard"
      >
        <WizardStepIndicator currentStep={currentStep} />

        <div className="flex-1 mt-lg">
          {currentStep === WIZARD_STEPS.STARTING_POINT && (
            <StartingPointStep
              control={control}
              onNext={() => goToStep(WIZARD_STEPS.TEMPLATE_STRUCTURE)}
              onFileUpload={handleFileUpload}
              loadingExtraction={loadingExtraction}
              selectedMethod={selectedMethod}
              onMethodChange={setSelectedMethod}
            />
          )}

          {currentStep === WIZARD_STEPS.TEMPLATE_STRUCTURE && (
            <TemplateStructureStep
              control={control}
              onNext={() =>
                goToStep(WIZARD_STEPS.TEMPLATE_PREFERENCES)
              }
              onBack={() => goToStep(WIZARD_STEPS.STARTING_POINT)}
            />
          )}

          {currentStep === WIZARD_STEPS.TEMPLATE_PREFERENCES && (
            <TemplatePreferencesStep
              control={control}
              onNext={() => goToStep(WIZARD_STEPS.PREVIEW)}
              onBack={() => goToStep(WIZARD_STEPS.TEMPLATE_STRUCTURE)}
            />
          )}

          {currentStep === WIZARD_STEPS.PREVIEW && (
            <PreviewStep
              control={control}
              onBack={() =>
                goToStep(WIZARD_STEPS.TEMPLATE_PREFERENCES)
              }
              onKeepEditing={() => {
                clearPreview();
                goToStep(WIZARD_STEPS.TEMPLATE_STRUCTURE);
              }}
              onLooksGood={() => goToStep(WIZARD_STEPS.NAME_AND_SAVE)}
              loadingPreview={loadingPreview}
              previewContent={previewContent}
              handlePreview={handlePreview}
              handleClearSampleTranscript={
                handleClearSampleTranscript
              }
              previewSource={previewSource}
              setPreviewSource={setPreviewSource}
              selectedNote={selectedNote}
              onSelectNote={onSelectNote}
            />
          )}

          {currentStep === WIZARD_STEPS.NAME_AND_SAVE && (
            <NameAndSaveStep
              control={control}
              onBack={() => goToStep(WIZARD_STEPS.PREVIEW)}
              onSave={onSave}
              saving={saving}
            />
          )}
        </div>
      </DSCardWrapper>

      <DSModal
        isOpen={!!showSuccessModal}
        onClose={onCloseSuccessModal}
        title="Your Template is Ready!"
        subtitle="Your template has been saved. What would you like to do next?"
        dataTestId="wizard-success-modal"
        actions={[
          {
            label: "Close",
            variant: "cancel",
            onClick: onCloseSuccessModal,
          },
        ]}
      >
        <div className="flex flex-col gap-md">
          <button
            type="button"
            onClick={() => navigate("/dashboard")}
            className="flex items-center gap-md p-md rounded-large border border-secondary bg-secondary hover:border-brand transition-colors text-left"
            data-testid="wizard-success-use-template"
          >
            <FontAwesomeIcon
              icon={faArrowRight}
              className="text-brand text-xl shrink-0"
            />
            <div className="flex-1">
              <h5 className="text-base font-semibold text-primary">
                Use this Template
              </h5>
              <p className="text-sm text-secondary">
                Go to the dashboard and start a recording with your
                new template.
              </p>
            </div>
          </button>

          <button
            type="button"
            onClick={onShareTemplate}
            className="flex items-center gap-md p-md rounded-large border border-secondary bg-secondary hover:border-brand transition-colors text-left"
            data-testid="wizard-success-share-template"
          >
            <FontAwesomeIcon
              icon={faShareNodes}
              className="text-brand text-xl shrink-0"
            />
            <div className="flex-1">
              <h5 className="text-base font-semibold text-primary">
                Share this Template
              </h5>
              <p className="text-sm text-secondary">
                Share with your team or generate a public link for
                others.
              </p>
            </div>
          </button>
        </div>
      </DSModal>
    </div>
  );
}
